<template>
  <div class="login-page d-flex flex-column flex-lg-row flex-column-fluid">
    <!--begin::Aside-->
    <div class="login-aside d-flex flex-column flex-lg-row-auto w-xl-600px position-xl-relative">
      <div class="d-flex flex-column position-xl-fixed top-0 bottom-0 w-xl-600px scroll-y">
        <div class="d-flex flex-column flex-center flex-grow-1 p-10 pt-lg-20">
          <h1 class="fw-bolder fs-2qx pb-5 pb-md-8 login-title">{{ $t('welcome') }}</h1>
          <p class="fw-bold fs-2 login-subtitle">
            {{ $t('welcome_text_start') }}
            <br /> {{ $t('welcome_text_end') }}
          </p>
          <div class="d-flex justify-content-center align-items-center mt-10">
            <img alt="Logo" src="/assets/media/r2b_login.jpg" class="login-hero-img" />
          </div>
        </div>
        <div class="login-aside-logo">
          <img alt="Qomerxia" src="/assets/media/logos/qomerxia-logo.png" class="h-20px" />
        </div>
      </div>
    </div>
    <!--end::Aside-->

    <!--begin::Body-->
    <div class="d-flex flex-column flex-lg-row-fluid">
      <!--begin::Language selector-->
      <div class="d-flex justify-content-end p-5">
        <div class="btn btn-icon btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px w-md-40px h-md-40px"
             data-kt-menu-trigger="hover" data-kt-menu-attach="parent" data-kt-menu-placement="bottom-end">
          <img class="h-20px w-20px rounded-sm" :src="currentLanguage.img" alt="" />
        </div>
        <div class="menu menu-sub menu-sub-dropdown menu-column w-150px" data-kt-menu="true">
          <div v-for="lang in langs" :key="lang.code" class="menu-item px-3">
            <a @click.prevent="changeLang(lang)" href="#" class="menu-link d-flex px-5 active">
              <span class="symbol symbol-20px me-4">
                <img class="rounded-1" :src="lang.img" :alt="$t(lang.title)" />
              </span>{{ $t(lang.title) }}
            </a>
          </div>
        </div>
      </div>
      <!--end::Language selector-->

      <!--begin::Form section-->
      <div class="d-flex flex-center flex-column flex-grow-1">
        <div class="login-form-card w-lg-500px p-10 p-lg-15 mx-auto">
          <form class="form w-100" @submit.prevent="submitForgotPassword" id="kt_password_reset_form">
            <!--begin::Heading-->
            <div class="text-center mb-11">
              <a href="">
                <img alt="Logo" src="/assets/media/logos/qomerxia-logo.png" class="h-45px mb-8" />
              </a>
              <h1 class="text-dark fw-bolder fs-2 mb-1">{{ $t('auth.forgot_password') }}</h1>
              <div class="login-separator my-6"></div>
              <div v-if="!otpSent" class="text-gray-400 fw-bold fs-5">
                {{ resetMode === 'email' ? $t('auth.enter_mail') : $t('auth.enter_phone_for_reset') }}
              </div>
              <div v-else-if="!otpVerified" class="text-gray-400 fw-bold fs-5">{{ $t('auth.enter_otp_code') }}</div>
              <div v-else class="text-gray-400 fw-bold fs-5">{{ $t('auth.new_password') }}</div>
            </div>
            <!--end::Heading-->

            <template v-if="!otpSent">
              <!--begin::Email or phone-->
              <div class="fv-row mb-8">
                <label class="form-label fs-6 fw-bolder text-dark mb-2">{{ $t('auth.email_or_phone') }}</label>

                <div v-if="resetMode === 'email'" class="position-relative">
                  <span class="login-input-icon login-input-icon-clickable login-input-icon-right"
                        :title="$t('auth.login_with_phone')"
                        @click="toggleResetMode">
                    <i class="bi bi-telephone fs-4"></i>
                  </span>
                  <input v-model="form.email" class="form-control form-control-lg form-control-solid login-input login-input-right-icon"
                         type="email" autocomplete="off" :placeholder="$t('auth.email')" />
                </div>

                <div v-else class="position-relative">
                  <span class="login-input-icon login-input-icon-clickable login-input-icon-right"
                        :title="$t('auth.login_with_email')"
                        @click="toggleResetMode">
                    <i class="bi bi-envelope fs-4"></i>
                  </span>
                  <InputTel
                      name="forgot_password_phone"
                      containerClass=""
                      v-model:phone="form.phone"
                      v-model:phone-code="form.phone_code"
                      :placeholder="$t('auth.phone')"
                      :isRequired="false"
                  />
                </div>
              </div>
              <!--end::Email or phone-->

              <!--begin::Actions-->
              <div class="d-flex flex-wrap justify-content-center gap-3 mt-10">
                <button type="submit" id="kt_submit_email_forgotten" class="btn btn-lg btn-primary login-btn flex-grow-1">
                  <span class="indicator-label fw-bold">{{ $t('button.send') }}</span>
                  <span class="indicator-progress">{{ $t('loading') }}
                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
                <router-link :to="'/login'" class="btn btn-lg btn-light login-btn-light flex-grow-1">{{ $t('button.cancel') }}</router-link>
              </div>
              <!--end::Actions-->
            </template>

            <template v-else-if="!otpVerified">
              <!--begin::OTP code (6 chiffres, vérification auto)-->
              <div class="fv-row mb-4">
                <div class="d-flex justify-content-center gap-2">
                  <input
                      v-for="(digit, index) in otpDigits"
                      :key="index"
                      :ref="el => otpInputRefs[index] = el"
                      v-model="otpDigits[index]"
                      @input="onOtpDigitInput(index, $event)"
                      @keydown="onOtpKeydown(index, $event)"
                      @paste="onOtpPaste($event)"
                      type="text"
                      inputmode="numeric"
                      maxlength="1"
                      autocomplete="off"
                      class="form-control form-control-lg form-control-solid text-center otp-box"
                      :disabled="isVerifyingOtp"
                  />
                </div>
              </div>
              <!--end::OTP code-->

              <div class="text-center mb-8">
                <span v-if="resendCountdown > 0" class="text-muted fs-7">
                  {{ $t('auth.resend_code_in', { seconds: resendCountdown }) }}
                </span>
                <a v-else href="#" class="link-primary fs-7 fw-bold" @click.prevent="resendOtpCode">
                  {{ $t('auth.resend_code') }}
                </a>
              </div>

              <!--begin::Actions-->
              <div class="d-flex flex-wrap justify-content-center gap-3">
                <router-link :to="'/login'" class="btn btn-lg btn-light login-btn-light flex-grow-1">{{ $t('button.cancel') }}</router-link>
              </div>
              <!--end::Actions-->
            </template>

            <template v-else>
              <!--begin::New password-->
              <div class="fv-row mb-8">
                <label class="form-label fs-6 fw-bolder text-dark mb-2">{{ $t('auth.password') }}</label>
                <div class="position-relative">
                  <span class="login-input-icon">
                    <i class="bi bi-lock fs-4"></i>
                  </span>
                  <input v-model="resetForm.password" class="form-control form-control-lg form-control-solid login-input login-input-password"
                         :type="showPassword ? 'text' : 'password'" autocomplete="off" :placeholder="$t('auth.password')" />
                  <span class="login-eye-icon" @click="showPassword = !showPassword">
                    <i :class="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'" class="fs-4"></i>
                  </span>
                </div>
              </div>
              <!--end::New password-->

              <!--begin::Confirm password-->
              <div class="fv-row mb-8">
                <label class="form-label fs-6 fw-bolder text-dark mb-2">{{ $t('auth.cf_password') }}</label>
                <div class="position-relative">
                  <span class="login-input-icon">
                    <i class="bi bi-lock-fill fs-4"></i>
                  </span>
                  <input v-model="resetForm.password_confirmation" class="form-control form-control-lg form-control-solid login-input login-input-password"
                         :type="showConfirmPassword ? 'text' : 'password'" autocomplete="off" :placeholder="$t('auth.cf_password')" />
                  <span class="login-eye-icon" @click="showConfirmPassword = !showConfirmPassword">
                    <i :class="showConfirmPassword ? 'bi bi-eye' : 'bi bi-eye-slash'" class="fs-4"></i>
                  </span>
                </div>
              </div>
              <!--end::Confirm password-->

              <!--begin::Actions-->
              <div class="d-flex flex-wrap justify-content-center gap-3 mt-10">
                <button type="button" id="kt_submit_otp_reset" class="btn btn-lg btn-primary login-btn flex-grow-1" @click="confirmResetPasswordByPhone">
                  <span class="indicator-label fw-bold">{{ $t('button.send') }}</span>
                  <span class="indicator-progress">{{ $t('loading') }}
                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
                <router-link :to="'/login'" class="btn btn-lg btn-light login-btn-light flex-grow-1">{{ $t('button.cancel') }}</router-link>
              </div>
              <!--end::Actions-->
            </template>
          </form>
        </div>
      </div>
      <!--end::Form section-->

      <!--begin::Footer-->
      <div class="d-flex flex-center py-5">
        <span class="text-muted fs-7">&copy; {{ new Date().getFullYear() }}</span>
      </div>
      <!--end::Footer-->
    </div>
    <!--end::Body-->
  </div>
</template>

<script>
import {mapActions} from "vuex";
import InputTel from '@/components/InputTel.vue'

export default {
  name: 'ForgotPasswordView',
  components: {
    InputTel,
  },
  data(){
    return {
      resetMode: 'email',
      otpSent: false,
      otpVerified: false,
      otpDigits: ['', '', '', '', '', ''],
      otpInputRefs: [],
      isVerifyingOtp: false,
      resendCountdown: 0,
      resendTimer: null,
      showPassword: false,
      showConfirmPassword: false,
      form : {
        email : '',
        phone: '',
        phone_code: ''
      },
      resetForm: {
        password: '',
        password_confirmation: ''
      },
      langs: {
        en : {
          'img': '/assets/media/united-states.svg',
          'title': 'header.language.english',
          'code': 'en'
        },
        fr : {
          'img': '/assets/media/france.svg',
          'title': 'header.language.french',
          'code': 'fr'
        },
      }
    }
  },
  computed : {
    currentLanguage(){
      return this.langs[this.$i18n?.locale]
    },
    otpCode(){
      return this.otpDigits.join('')
    },
  },
  mounted(){
  },
  beforeUnmount(){
    clearInterval(this.resendTimer)
  },

  methods: {
    ...mapActions('auth', [
      'forgetPassword',
      'forgetPasswordByPhone',
      'verifyPhoneResetOtp',
      'resetPasswordByPhone'
    ]),
    changeLang(lang){
      this.$i18n.locale = lang.code
      localStorage.setItem('locale', lang.code)
    },
    toggleResetMode() {
      if (this.resetMode === 'email') {
        this.resetMode = 'phone'
        this.form.email = ''
      } else {
        this.resetMode = 'email'
        this.form.phone = ''
        this.form.phone_code = ''
      }
    },
    async submitForgotPassword(){
      if (this.otpVerified) {
        return this.confirmResetPasswordByPhone()
      }
      if (this.otpSent) {
        return this.verifyOtpCode()
      }

      this.$loading('kt_submit_email_forgotten')

      try{
        if (this.resetMode === 'email') {
          await this.forgetPassword(this.form)
          this.form.email = ''
          Swal.fire({
            text: i18n.global.t('auth.reset_password_email_sent'),
            icon: "success",
            showCancelButton: false,
            buttonsStyling: false,
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          })
        } else {
          await this.forgetPasswordByPhone(this.form)
          window.toastr['success'](i18n.global.t('auth.reset_password_phone_sent'))
          this.otpSent = true
          this.startResendCountdown()
          this.$nextTick(() => this.otpInputRefs[0]?.focus())
        }

        this.$stopLoading('kt_submit_email_forgotten')
        return true

      }catch(err){
        this.$stopLoading('kt_submit_email_forgotten')
        return true
      }
    },
    startResendCountdown(){
      clearInterval(this.resendTimer)
      this.resendCountdown = 60
      this.resendTimer = setInterval(() => {
        this.resendCountdown--
        if (this.resendCountdown <= 0) {
          clearInterval(this.resendTimer)
        }
      }, 1000)
    },
    async resendOtpCode(){
      if (this.resendCountdown > 0) {
        return
      }
      try{
        await this.forgetPasswordByPhone(this.form)
        window.toastr['success'](i18n.global.t('auth.reset_password_phone_sent'))
        this.otpDigits = ['', '', '', '', '', '']
        this.startResendCountdown()
        this.$nextTick(() => this.otpInputRefs[0]?.focus())
      }catch(err){
        // géré par l'intercepteur axios global
      }
    },
    onOtpDigitInput(index, event){
      const value = event.target.value.replace(/\D/g, '').slice(-1)
      this.otpDigits[index] = value

      if (value && index < this.otpDigits.length - 1) {
        this.otpInputRefs[index + 1]?.focus()
      }

      if (this.otpCode.length === 6) {
        this.verifyOtpCode()
      }
    },
    onOtpKeydown(index, event){
      if (event.key === 'Backspace' && ! this.otpDigits[index] && index > 0) {
        this.otpInputRefs[index - 1]?.focus()
      }
    },
    onOtpPaste(event){
      const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6)
      if (! pasted) {
        return
      }
      event.preventDefault()
      this.otpDigits = pasted.split('').concat(['', '', '', '', '', '']).slice(0, 6)
      this.$nextTick(() => {
        const nextIndex = Math.min(pasted.length, 5)
        this.otpInputRefs[nextIndex]?.focus()
        if (this.otpCode.length === 6) {
          this.verifyOtpCode()
        }
      })
    },
    async verifyOtpCode(){
      if (this.otpCode.length !== 6 || this.isVerifyingOtp) {
        return
      }
      this.isVerifyingOtp = true

      try{
        await this.verifyPhoneResetOtp({
          phone: this.form.phone,
          phone_code: this.form.phone_code,
          code: this.otpCode,
        })
        this.otpVerified = true
        clearInterval(this.resendTimer)
      }catch(err){
        window.toastr['error'](err.response?.data?.message || i18n.global.t('auth.invalid_otp_code'))
        this.otpDigits = ['', '', '', '', '', '']
        this.$nextTick(() => this.otpInputRefs[0]?.focus())
      }finally{
        this.isVerifyingOtp = false
      }
    },
    async confirmResetPasswordByPhone(){
      this.$loading('kt_submit_otp_reset')

      try{
        const data = {
          phone: this.form.phone,
          phone_code: this.form.phone_code,
          code: this.otpCode,
          password: this.resetForm.password,
          password_confirmation: this.resetForm.password_confirmation,
        }
        const response = await this.resetPasswordByPhone(data)
        window.toastr['success'](response.data.message)
        this.$stopLoading('kt_submit_otp_reset')
        this.$router.push('/login')
        return true

      }catch(err){
        this.$stopLoading('kt_submit_otp_reset')
        return true
      }
    },
  },

  updated() {
    this.$nextTick(() => {
      KTApp.init();
    })
  },
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
}

/* Aside panel */
.login-aside {
  background: linear-gradient(135deg, rgba(var(--platform-primary-rgb, 11, 61, 135), 0.06) 0%, #fff 50%, rgba(var(--platform-primary-rgb, 11, 61, 135), 0.1) 100%);
  height: 100vh;
  box-shadow: 4px 0 24px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.15);
}

.login-title {
  color: #1a1a2e;
}

.login-subtitle {
  color: #4a4a68;
  line-height: 1.6;
}

.login-hero-img {
  max-height: 360px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.2);
  object-fit: cover;
}

.login-aside-logo {
  position: absolute;
  bottom: 24px;
  right: 24px;
  opacity: 0.7;
}

/* Form card */
.login-form-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
}

.login-separator {
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--platform-primary, #0b3d87), transparent);
  border-radius: 1px;
}

/* Input with icon */
.login-input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #b5b5c3;
  z-index: 3;
  pointer-events: none;
}

.login-input-icon-clickable {
  pointer-events: auto;
  cursor: pointer;
  color: var(--platform-primary, #0b3d87);
  background: #ffffff;
  border: 1px solid rgba(var(--platform-primary-rgb, 11, 61, 135), 0.4);
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.login-input-icon-clickable:hover {
  background: var(--platform-primary, #0b3d87) !important;
  color: #ffffff !important;
  transform: translateY(-50%) scale(1.05);
}

.login-input-icon-clickable:hover i {
  color: #ffffff !important;
}

.login-input-icon-right {
  left: auto;
  right: 8px;
}

.login-input {
  padding-left: 42px !important;
  border-radius: 10px !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.login-input-right-icon {
  padding-left: 16px !important;
  padding-right: 56px !important;
}

.login-input-password {
  padding-right: 42px !important;
}

.login-eye-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #b5b5c3;
  cursor: pointer;
  z-index: 3;
  transition: color 0.2s ease;
}

.login-eye-icon:hover {
  color: var(--platform-primary, #0b3d87);
}

.login-input:focus {
  border-color: var(--platform-primary, #0b3d87) !important;
  box-shadow: 0 0 0 3px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.15) !important;
}

:deep(.vue-tel-input) {
  border-radius: 10px !important;
  overflow: hidden;
}

:deep(.vti__input) {
  padding-right: 56px !important;
}

:deep(.vue-tel-input:focus-within) {
  border-color: var(--platform-primary, #0b3d87) !important;
  box-shadow: 0 0 0 3px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.15) !important;
}

/* Buttons */
.login-btn {
  border-radius: 10px !important;
  padding: 12px 0 !important;
  font-size: 1.1rem !important;
  letter-spacing: 0.3px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.35);
}

.login-btn:active:not(:disabled) {
  transform: translateY(0);
}

.login-btn-light {
  border-radius: 10px !important;
  padding: 12px 0 !important;
  font-size: 1.1rem !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.login-btn-light:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* OTP boxes */
.otp-box {
  width: 52px;
  height: 60px;
  padding: 0 !important;
  font-size: 1.5rem !important;
  font-weight: 700;
  border-radius: 10px !important;
}

.otp-box:focus {
  border-color: var(--platform-primary, #0b3d87) !important;
  box-shadow: 0 0 0 3px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.15) !important;
}

/* Responsive */
/* Mobile et tablette : formulaire seul, sur la hauteur visible de l'écran (100dvh exclut la
   barre d'adresse, contrairement à 100vh) — le panneau d'accueil et son image sont masqués */
@media (max-width: 991.98px) {
  .login-page {
    min-height: 100dvh;
  }

  .login-aside {
    display: none !important;
  }

  .login-form-card {
    box-shadow: none;
    padding: 1.5rem !important;
  }

  .login-form-card .mb-11 {
    margin-bottom: 1.5rem !important;
  }

  .login-form-card .mb-8 {
    margin-bottom: 1.25rem !important;
  }

  .login-form-card .mt-10 {
    margin-top: 1.75rem !important;
  }
}
</style>
